/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { dwService } from 'amb/services/dw.service';

/**
 * Countdown options container.
 *
 * Available presets:
 *
 *  - `default`     Always applied first. Fallback values used when the server
 *                  (countdown.component.js) didn't emit a particular option.
 *                  In Page-Designer flow the server emits every option, so the
 *                  defaults below are mostly relevant for non-PD usage (e.g.,
 *                  a developer hand-rolling `<div amb-countdown data-options="...">`).
 *  - `flash_sale`  Tight UX for sub-day countdowns: trims leading zero units;
 *                  clock only renders during the Active state.
 *  - `compact`     Compact tile-friendly variant: drops the `days` unit and
 *                  caps the rendered units to 3 (sliding window).
 *
 * Site-wide overrides per preset can be injected via the `ambCountdownOptions`
 * key of the `directives` Site Preference.
 */
export const countdownOptions = {
  /**
   * Default options applied to every countdown instance.
   *
   * @param {object}      params
   * @param {HTMLElement} params.element
   */
  default({ element }) {
    return {
      /**
       * Lifecycle statuses for which the wrapper element is rendered. When
       * the resolved status is NOT in this list the wrapper gets the `hidden`
       * attribute and no clock ticks.
       *
       * @type {Array<'upcoming' | 'active' | 'ended'>}
       */
      showComponentOnStatus: ['upcoming', 'active', 'ended'],

      /**
       * Lifecycle statuses for which the digital clock is rendered. When the
       * status is not in this list (or the clock cannot be rendered, e.g., an
       * Active state with no `endDate`) the label still shows but `{clock}`
       * tokens are stripped and no fallback clock is appended.
       *
       * @type {Array<'upcoming' | 'active' | 'ended'>}
       */
      showClockOnStatus: ['upcoming', 'active'],

      /**
       * Label copy per lifecycle state. Supports `{clock}` and `{clock:units}`
       * tokens (see countdown.directive.js).
       */
      labelUpcoming: 'Starts in {clock}',
      labelActive: 'Ends in {clock}',
      labelEnded: 'Ended',

      /**
       * Time-source timestamps. Stored internally as **millisecond epochs**
       * but the directive accepts ALL of these inputs and parses them on
       * `init`:
       *
       *  - `null`                   → unconfigured (skips the corresponding
       *                               lifecycle transition).
       *  - `number`                 → epoch ms (e.g., `1735689600000`).
       *  - `Date` instance          → unwrapped via `.getTime()`.
       *  - numeric string           → parsed as epoch ms (`"1735689600000"`).
       *  - ISO 8601 with `Z`        → UTC moment (e.g., `"2024-07-23T00:00:00Z"`).
       *  - ISO 8601 without `Z`     → local-time moment.
       *  - `YYYY-MM-DD`             → UTC midnight per ISO 8601 spec.
       *
       * `null` (the default) means unconfigured — a missing `startDate`
       * skips the Upcoming state, a missing `endDate` renders Active without
       * a clock.
       *
       * @type {number | string | Date | null}
       */
      startDate: null,
      endDate: null,

      /**
       * Pool of time units the clock may use, ordered from largest to smallest.
       * Disabling a higher unit overflows its value into the next visible
       * lower unit.
       *
       * @type {Array<'days' | 'hours' | 'minutes' | 'seconds'>}
       */
      visibleUnits: ['days', 'hours', 'minutes', 'seconds'],

      /**
       * Maximum number of units rendered at once. The clock slides to show the
       * most relevant units based on the remaining time. `0` means "no cap —
       * show all visible units".
       *
       * @type {number}
       */
      maxVisibleUnits: 0,

      /**
       * @type {'single_block' | 'split_blocks'}
       */
      clockLayout: 'split_blocks',

      /**
       * Format pattern for each unit:
       *  - `digital`        → `01 : 02 : 03 : 04`         (no suffix)
       *  - `short_suffix`   → `01d : 02h : 03m : 04s`     (single-letter suffix)
       *  - `full_suffix`    → `1 Day : 2 Hours : ...`     (localized full word)
       *
       * @type {'digital' | 'short_suffix' | 'full_suffix'}
       */
      pattern: 'short_suffix',

      /**
       * Text rendered between unit blocks. CSS `gap` still applies on top.
       *
       * @type {string}
       */
      unitSeparator: ':',

      /**
       * When true, single-digit values are zero-padded to two digits
       * (`7` → `07`) via NumberFlow's `minimumIntegerDigits` formatter.
       *
       * Page Designer exposes this as the opt-out `disablePadDigits` flag
       * (unchecked = padding ON).
       *
       * @type {boolean}
       */
      padDigits: true,

      /**
       * Hides leading time units whose value is zero (e.g., `0d 0h 5m 22s`
       * renders as `5m 22s`). The lowest visible unit is always kept, even if
       * zero. Has no effect when `maxVisibleUnits > 0` (sliding window already
       * trims leading zeros).
       *
       * Page Designer exposes this as the opt-out `showZeroUnits` flag
       * (unchecked = hide leading zeros ON).
       *
       * @type {boolean}
       */
      hideZeroUnits: true,

      /**
       * Per-unit minimum value before the unit appears in the clock. When a
       * unit's tentative value is below its threshold, the unit is removed
       * from the effective pool and its mass overflows into the next smaller
       * unit (e.g., `unitThresholds.days = 2` turns `1d 10h 20m` into
       * `34h 20m`). A value of `0` disables thresholding for that unit.
       *
       * Applied AFTER `visibleUnits` but BEFORE `maxVisibleUnits` and
       * `hideZeroUnits`, so the sliding window and zero-trim operate on the
       * already-collapsed pool.
       *
       * @type {{ days: number, hours: number, minutes: number, seconds: number }}
       */
      unitThresholds: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },

      /**
       * Localized unit suffixes resolved by the server. The client picks the
       * right entry based on `pattern` and (for `full_suffix`) the current
       * value's singular/plural form.
       *
       * @type {{
       *   short: { days: string, hours: string, minutes: string, seconds: string },
       *   full: {
       *     days:    { singular: string, plural: string },
       *     hours:   { singular: string, plural: string },
       *     minutes: { singular: string, plural: string },
       *     seconds: { singular: string, plural: string }
       *   }
       * }}
       */
      suffixes: {
        short: {
          days: dwService.msg('common.countdown.unit.days.short', 'experience'),
          hours: dwService.msg('common.countdown.unit.hours.short', 'experience'),
          minutes: dwService.msg('common.countdown.unit.minutes.short', 'experience'),
          seconds: dwService.msg('common.countdown.unit.seconds.short', 'experience')
        },
        full: {
          days: {
            singular: dwService.msg('common.countdown.unit.days.full.singular', 'experience'),
            plural: dwService.msg('common.countdown.unit.days.full.plural', 'experience')
          },
          hours: {
            singular: dwService.msg('common.countdown.unit.hours.full.singular', 'experience'),
            plural: dwService.msg('common.countdown.unit.hours.full.plural', 'experience')
          },
          minutes: {
            singular: dwService.msg('common.countdown.unit.minutes.full.singular', 'experience'),
            plural: dwService.msg('common.countdown.unit.minutes.full.plural', 'experience')
          },
          seconds: {
            singular: dwService.msg('common.countdown.unit.seconds.full.singular', 'experience'),
            plural: dwService.msg('common.countdown.unit.seconds.full.plural', 'experience')
          }
        }
      },

      /**
       * Visual appearance options. The directive applies the corresponding
       * `countdown-{value}` class on init (idempotent — the Page Designer
       * server already adds these for the SSR/no-FOUC case). For direct
       * usage in ISML / content assets, the directive ensures classes are
       * applied even when no server logic ran.
       *
       * Typography (font-family / font-size / line-height / base weight)
       * is NOT a directive concern — it comes from the styleguide via
       * any of the standard typography classes (`.h1`–`.h6`,
       * `.sub-title-{size}`, `.text-{size}`) applied directly on the host
       * element. The countdown's digits and chrome size from that font via
       * `em` units, so they scale proportionally.
       *
       * @type {string}
       */
      clockColor: 'primary',

      /**
       * @type {'filled' | 'outlined'}
       */
      clockStyle: 'filled'
    };
  },

  /**
   * Flash-sale preset: tighter UX for sub-day countdowns.
   *
   *  - Hides leading zero units (so `0d 0h 5m 22s` renders as `5m 22s`).
   *  - Clock only renders during the Active state — Upcoming/Ended are
   *    static text-only labels.
   */
  flash_sale: {
    hideZeroUnits: true,
    showClockOnStatus: ['active']
  },

  /**
   * Compact preset for product tiles and grid cells.
   *
   *  - Drops the `days` unit; remaining time overflows into `hours`.
   *  - Caps the sliding window to 3 visible units.
   */
  compact: {
    visibleUnits: ['hours', 'minutes', 'seconds'],
    maxVisibleUnits: 3
  },

  /**
   * Promotion-tile preset: a compact, badge-friendly clock used by the
   * Promotion Tile schedule badge when a promotion is within its countdown
   * threshold.
   *
   *  - Trims leading zero units for a tight label.
   *  - Slides to at most 2 units (e.g. `2d 5h`, then `5h 30m`).
   *  - Short single-letter suffixes in a single block.
   */
  promotion_tile: {
    hideZeroUnits: true,
    maxVisibleUnits: 3,
    pattern: 'short_suffix',
    clockLayout: 'single_block'
  }
};
